import React, { useMemo } from 'react';
import { AnswerInput } from 'shared/providers/ProgramProvider';
import BudgetUpload from 'npo/pages/ReportBuilder/Components/Budget/BudgetUpload';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { GuidanceTextResponse } from 'npo/pages/ReportBuilder/Components/utils';
import { ReportsTable } from '../ReportGroup';

const emptyGuidance: GuidanceTextResponse = {};

const defaultFileValue: TrueImpactFile = {
  fileName: undefined,
  description: undefined,
};

// Mirrors getFileInfo() in ReportBuilder/Components/Budget/index.tsx so the
// legacy answer format (JSON blob with fileName/description/id/url) rehydrates
// the file list correctly.
const getFileInfo = (raw: string | null | undefined): TrueImpactFile => {
  if (!raw) return defaultFileValue;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.fileName) return parsed;
    if (parsed?.description) return { description: parsed.description };
  } catch {
    // fall through
  }
  return defaultFileValue;
};

interface Props {
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  report: ReportsTable;
}

const noop = () => undefined;

// Adapts BudgetUpload (question 80009) to the ReportsBeta program-question
// flow: internal BottomBar is hidden, and BudgetUpload's onValueChange emitter
// keeps AnswerInput in sync so the outer wizard footer persists via the
// standard save flow.
const BudgetUploadField: React.FC<Props> = ({ value, onChange, report }) => {
  const initialValue = useMemo(() => getFileInfo(value.text), [value.text]);

  const handleValueChange = (
    filesJson: string,
    description: string | undefined,
  ) => {
    onChange({
      ...value,
      text: JSON.stringify({
        fileName: filesJson,
        description,
        id: undefined,
        url: undefined,
      }),
    });
  };

  return (
    <BudgetUpload
      initialValue={initialValue}
      reportId={report.dbId}
      guidanceText={emptyGuidance}
      hideBottomBar={true}
      onNext={noop}
      onPrevious={noop}
      onValueChange={handleValueChange}
    />
  );
};

export default BudgetUploadField;
